import { CloseRoundLight, DoneLight, SettingLineLight } from '@repo/go-icons';
import { ActionsBar, Button, ConfigContext, Switch, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { TimezoneSwitch } from '../../../shared/components/TimezoneSwitch/TimezoneSwitch';
import type { AlertsManagerWidgetState } from '../../AlertsManagerWidgetState';
import type { ManualAnomaliesTableStore } from '../ManualAnomaliesTableStore';
import type { ManualAnomaliesService } from '../services/ManualAnomaliesService';

export const ManualAnomaliesViewActionsBar = observer(function ManualAnomaliesViewActionsBar({
  service,
  widgetState,
  store,
}: {
  service: ManualAnomaliesService;
  widgetState: AlertsManagerWidgetState;
  store: ManualAnomaliesTableStore;
}) {
  const { t, i18n } = useTranslation();
  const configProvider = useContext(ConfigContext);
  const editing = service.uiState.editing;

  const save = () => {
    service
      .saveManualAnomalies()
      .then(() => {
        service.setEditing(false);
        widgetState.eventHandlers.onEditModeChanged?.(false);
      })
      .catch((error) => {
        console.error(error);
      });
  };
  const cancel = () => {
    widgetState.exitEditModeAsync(configProvider, i18n).catch((error: unknown) => {
      console.error(error);
    });
  };

  return (
    <ActionsBar>
      <ActionsBar.TopBar>
        {editing && (
          <>
            <ActionsBar.Space>
              <Tooltip placement="left" title={t('alertsManagerWidget:actionsBar.save')} delay={400}>
                <div>
                  <Button icon={<DoneLight />} variant="success" onClick={() => save()} />
                </div>
              </Tooltip>
            </ActionsBar.Space>

            <ActionsBar.Space>
              <Tooltip placement="left" title={t('alertsManagerWidget:actionsBar.cancelChanges')} delay={400}>
                <div>
                  <Button icon={<CloseRoundLight />} variant="danger" onClick={() => cancel()} />
                </div>
              </Tooltip>
            </ActionsBar.Space>
          </>
        )}
      </ActionsBar.TopBar>
      <ActionsBar.BottomBar>
        <Tooltip placement="left" title={t('alertsManagerWidget:tableSettings')} delay={400}>
          <ActionsBar.Item active={store.isTableSettingsOpen} onClick={() => store.setIsTableSettingsOpen(true)}>
            <SettingLineLight />
          </ActionsBar.Item>
        </Tooltip>
        <TimezoneSwitch timeZones={widgetState.timeZonesStore} />
        <Switch
          style={{ marginLeft: 4 }}
          orientation="vertical"
          size="m"
          checked={widgetState.settingsStore.scaleDepthUnit === 'ft'}
          leftNode={'m'}
          rightNode={'ft'}
          onChange={() =>
            widgetState.settingsStore.setScaleDepthUnit(widgetState.settingsStore.scaleDepthUnit === 'm' ? 'ft' : 'm')
          }
        />
      </ActionsBar.BottomBar>
    </ActionsBar>
  );
});
